import { useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Plus,
  RotateCcw,
  Send,
  ShieldCheck,
  Trash2,
  Upload,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data-table/DataTable';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import { BulkEditDialog, type BulkEditField } from '@/components/data-table/BulkEditDialog';
import { Download, FileCheck2, Pencil } from 'lucide-react';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllActivites } from '@/hooks/queries/useActivites';
import { useTiers } from '@/hooks/queries/useTiers';
import { useSoldes } from '@/hooks/queries/useSoldes';
import {
  useBulkContrePassation,
  useBulkDeleteOperations,
  useBulkUpdateOperationStatus,
  useContrePassation,
  useDeleteOperation,
  useOperations,
  useUpdateOperationStatus,
  type OperationsFilter,
} from '@/hooks/queries/useOperations';
import { ImportDialog, type ImportField } from '@/components/import/ImportDialog';
import { buildTemplate, TEMPLATE_OPERATIONS } from '@/lib/import-templates';
import { ImportCaisseTripSunDialog } from '@/components/imports/ImportCaisseTripSunDialog';
import { ImportCaisseB2CDialog } from '@/components/imports/ImportCaisseB2CDialog';
import { CaisseDashboardSimple } from '@/components/finances/CaisseDashboardSimple';
import { EditOperationDialog } from '@/components/forms/EditOperationDialog';
import { supabase } from '@/lib/supabase';
import { useRole } from '@/hooks/useRole';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';
import { formatDA, formatDateTimeDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type {
  OperationCanal,
  OperationCategorie,
  OperationRow,
  OperationSens,
  OperationStatut,
} from '@/lib/database.types';
import { OperationCaisseFormDialog } from './OperationCaisseFormDialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const SENS_VARIANT: Record<OperationSens, 'success' | 'warning' | 'outline'> = {
  entree: 'success',
  sortie: 'warning',
  transfert: 'outline',
};

const STATUT_VARIANT: Record<
  OperationStatut,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline'
> = {
  brouillon: 'outline',
  en_attente: 'warning',
  validee: 'default',
  approuvee: 'success',
  comptabilisee: 'secondary',
  annulee: 'destructive',
};

const CATEGORIE_LABELS: Record<OperationCategorie, string> = {
  vente_service: 'Vente service',
  vente_marchandise: 'Vente marchandise',
  remboursement: 'Remboursement',
  achat_marchandise: 'Achat marchandise',
  sous_traitance: 'Sous-traitance',
  salaire: 'Salaire',
  charge_sociale: 'Charge sociale',
  loyer: 'Loyer',
  electricite: 'Électricité',
  telecom: 'Télécom',
  transport: 'Transport',
  fournitures: 'Fournitures',
  commission: 'Commission',
  taxe: 'Taxe',
  remise: 'Remise',
  transfert_interne: 'Transfert interne',
  avance_personnel: 'Avance personnel',
  autre: 'Autre',
};

export function OperationsPage(): JSX.Element {
  const { isAdmin, role } = useRole();
  const canViewCaisse = usePermission('caisse.view');
  const canCreateOp = usePermission('caisse.create_op');
  const { data: entreprises = [] } = useEntreprises();
  const { data: allActivites = [] } = useAllActivites();
  const [searchParams] = useSearchParams();

  const [entrepriseId, setEntrepriseId] = useState('');
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [filter, setFilter] = useState<OperationsFilter>({});

  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  // Lecture des query params (depuis ComptesPage "Voir les opérations")
  useEffect(() => {
    const compte = searchParams.get('compte');
    const type = searchParams.get('type');
    if (compte && type === 'banque') {
      setFilter((f) => ({ ...f, compteBancaireId: compte, caisseId: null }));
    } else if (compte && type === 'caisse') {
      setFilter((f) => ({ ...f, caisseId: compte, compteBancaireId: null }));
    }
  }, [searchParams]);

  const fullFilter: OperationsFilter = {
    ...filter,
    entrepriseId,
    search,
    page,
    pageSize: 50,
  };

  const { data, isLoading } = useOperations(fullFilter);
  const rows = data?.rows ?? [];
  const total = data?.count ?? 0;
  const { data: tiers = [] } = useTiers({ entrepriseId, search: '' });
  const { data: soldes = [] } = useSoldes(entrepriseId);

  const updateStatus = useUpdateOperationStatus();
  const removeOp = useDeleteOperation();
  const contrePassation = useContrePassation();

  const [caisseDialogOpen, setCaisseDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Sprint 6.9.4 — imports CSV Caisse spécifiques (Trip Sun + B2C)
  const [importTripSunOpen, setImportTripSunOpen] = useState(false);
  const [importB2COpen, setImportB2COpen] = useState(false);
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<OperationRow | null>(null);
  // Sprint 6.9.9 — Edit per-row
  const [editOperation, setEditOperation] = useState<OperationRow | null>(null);

  // Sprint 6.9.7 — Pre-flight import : 2-passes (analyse → confirmation user → INSERT batch)
  interface PreflightSummary {
    totalRows: number;
    validRows: number;
    missingActivites: { name: string; lines: number }[];
    missingComptes: { name: string; lines: number }[];
    newTiersClient: number;
    newTiersFournisseur: number;
    invalidLines: { reason: string; count: number }[];
    fuzzySuggestions: { missing: string; suggestion: string; kind: 'activite' | 'compte' }[];
  }
  const [preflight, setPreflight] = useState<PreflightSummary | null>(null);
  const preflightResolverRef = useRef<((decision: 'confirm' | 'cancel') => void) | null>(null);
  const askPreflight = (s: PreflightSummary): Promise<'confirm' | 'cancel'> => {
    setPreflight(s);
    return new Promise<'confirm' | 'cancel'>((resolve) => {
      preflightResolverRef.current = resolve;
    });
  };
  const resolvePreflight = (decision: 'confirm' | 'cancel'): void => {
    if (preflightResolverRef.current) {
      preflightResolverRef.current(decision);
      preflightResolverRef.current = null;
    }
    setPreflight(null);
  };

  // Sprint 6.9.7 — Toast erreurs groupé par cause unique
  const [errorDetail, setErrorDetail] = useState<{
    grouped: { reason: string; count: number; samples: string[] }[];
    flat: string[];
  } | null>(null);

  // Bulk selection
  const [selectedRows, setSelectedRows] = useState<OperationRow[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const bulkUpdateStatus = useBulkUpdateOperationStatus();
  const bulkDelete = useBulkDeleteOperations();
  const bulkContrePass = useBulkContrePassation();
  const clearSelection = (): void => setSelectionVersion((v) => v + 1);

  const selectedIds = selectedRows.map((r) => r.id);
  const allSelectedAreEnAttente =
    selectedRows.length > 0 && selectedRows.every((r) => r.statut === 'en_attente');
  const allSelectedAreValidee =
    selectedRows.length > 0 && selectedRows.every((r) => r.statut === 'validee');
  const allSelectedAreApprouvee =
    selectedRows.length > 0 && selectedRows.every((r) => r.statut === 'approuvee');
  const allSelectedAreComptabilisee =
    selectedRows.length > 0 && selectedRows.every((r) => r.statut === 'comptabilisee');
  // Sprint 6.9.6 — bulk delete autorisé pour tout sauf comptabilisée (cohérent RLS migration 105)
  const allSelectedAreSupprimables =
    selectedRows.length > 0 && selectedRows.every((r) => r.statut !== 'comptabilisee');

  const activitesScoped = useMemo(
    () => allActivites.filter((a) => a.entreprise_id === entrepriseId),
    [allActivites, entrepriseId],
  );

  const canValidate = role === 'comptable' || role === 'direction' || isAdmin;
  const canApprove = role === 'direction' || isAdmin;
  const canComptabiliser = role === 'comptable' || isAdmin;

  const tierMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const tt of tiers) m.set(tt.id, tt.raison_sociale);
    return m;
  }, [tiers]);

  const columns = useMemo<ColumnDef<OperationRow>[]>(
    () => [
      {
        accessorKey: 'reference',
        header: 'Réf.',
        size: 130,
        cell: ({ getValue }) => <span className="font-mono text-xs">{String(getValue())}</span>,
      },
      {
        accessorKey: 'date_operation',
        header: 'Date',
        size: 140,
        cell: ({ getValue }) => (
          <span className="num text-xs">{formatDateTimeDZ(String(getValue()))}</span>
        ),
      },
      {
        accessorKey: 'sens',
        header: 'Sens',
        size: 80,
        cell: ({ getValue }) => {
          const v = getValue() as OperationSens;
          return <Badge variant={SENS_VARIANT[v]}>{t(`finances.operations.sens.${v}`)}</Badge>;
        },
      },
      {
        accessorKey: 'canal',
        header: 'Canal',
        size: 90,
        cell: ({ getValue }) => (
          <Badge variant="outline">
            {t(`finances.operations.canal.${getValue() as OperationCanal}`)}
          </Badge>
        ),
      },
      {
        accessorKey: 'categorie',
        header: 'Catégorie',
        size: 140,
        cell: ({ getValue }) => (
          <span className="text-xs">{CATEGORIE_LABELS[getValue() as OperationCategorie]}</span>
        ),
      },
      { accessorKey: 'motif', header: 'Motif' },
      {
        accessorKey: 'tier_id',
        header: 'Tiers',
        size: 180,
        cell: ({ getValue }) => {
          const id = getValue() as string | null;
          return <span className="truncate text-xs">{id ? (tierMap.get(id) ?? '—') : '—'}</span>;
        },
      },
      {
        accessorKey: 'montant_dzd',
        header: 'Montant DA',
        size: 130,
        cell: ({ getValue, row }) => (
          <span
            className={cn('num', row.original.sens === 'entree' ? 'text-success' : 'text-warning')}
          >
            {row.original.sens === 'sortie' ? '−' : '+'}
            {formatDA(Number(getValue()))}
          </span>
        ),
      },
      {
        accessorKey: 'statut',
        header: 'Statut',
        size: 110,
        cell: ({ getValue }) => {
          const s = getValue() as OperationStatut;
          return <Badge variant={STATUT_VARIANT[s]}>{t(`finances.operations.statut.${s}`)}</Badge>;
        },
      },
      {
        id: 'actions',
        header: '',
        size: 220,
        enableSorting: false,
        cell: ({ row }) => {
          const op = row.original;
          return (
            <div className="flex flex-wrap items-center justify-end gap-1">
              {op.statut === 'brouillon' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={async () => {
                    await updateStatus.mutateAsync({ id: op.id, statut: 'en_attente' });
                    toast.success('Opération soumise');
                  }}
                >
                  <Send className="h-3 w-3" /> Soumettre
                </Button>
              )}
              {op.statut === 'en_attente' && canValidate && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={async () => {
                    await updateStatus.mutateAsync({ id: op.id, statut: 'validee' });
                    toast.success('Opération validée');
                  }}
                >
                  <CheckCircle2 className="h-3 w-3" /> Valider
                </Button>
              )}
              {op.statut === 'validee' && canApprove && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={async () => {
                    await updateStatus.mutateAsync({ id: op.id, statut: 'approuvee' });
                    toast.success('Opération approuvée');
                  }}
                >
                  <ShieldCheck className="h-3 w-3" /> Approuver
                </Button>
              )}
              {op.statut === 'approuvee' && canComptabiliser && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={async () => {
                    await updateStatus.mutateAsync({ id: op.id, statut: 'comptabilisee' });
                    toast.success('Opération comptabilisée');
                  }}
                >
                  <CheckCircle2 className="h-3 w-3" /> Comptabiliser
                </Button>
              )}
              {op.statut === 'comptabilisee' && canComptabiliser && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-destructive"
                  onClick={async () => {
                    await contrePassation.mutateAsync(op.id);
                    toast.success('Contre-passation effectuée');
                  }}
                >
                  <RotateCcw className="h-3 w-3" /> Contre-pass.
                </Button>
              )}
              {/* Sprint 6.9.9 — Modifier par-ligne (sauf comptabilisée) */}
              {op.statut !== 'comptabilisee' && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setEditOperation(op)}
                  aria-label="Modifier"
                  title="Modifier"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              {/* Sprint 6.9.5 — Delete par-ligne admin uniquement, sauf si comptabilisée
                  (passer par contre-passation pour celles-ci pour préserver la traçabilité) */}
              {op.statut !== 'comptabilisee' && isAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => setConfirmDelete(op)}
                  aria-label="Supprimer"
                  title="Supprimer (super_admin)"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [tierMap, isAdmin, canValidate, canApprove, canComptabiliser, updateStatus, contrePassation],
  );

  const totalSoldeDzd = soldes
    .filter((s) => s.devise_code === 'DZD')
    .reduce((acc, s) => acc + s.solde_courant, 0);
  const totalPages = Math.max(1, Math.ceil(total / 50));

  if (!canViewCaisse) {
    return <AccessDenied feature="caisse.view" />;
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={t('finances.operations.title')}
        subtitle={t('finances.operations.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            {/* Sprint 6.9.6 — UI simplifiée : un seul import (générique).
                Imports Trip Sun + B2C cachés derrière feature flag (?import_caisse_v2=1)
                en attendant validation des mappings sur les vrais CSV KOST. */}
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" /> Importer Excel
            </Button>
            {typeof window !== 'undefined' &&
              new URLSearchParams(window.location.search).get('import_caisse_v2') === '1' && (
                <div className="relative">
                  <Button variant="outline" onClick={() => setImportMenuOpen((v) => !v)}>
                    <Upload className="h-4 w-4" /> Caisse v2 (beta)
                  </Button>
                  {importMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setImportMenuOpen(false)}
                        aria-hidden
                      />
                      <div className="absolute right-0 z-20 mt-1 w-72 rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
                        <button
                          type="button"
                          onClick={() => {
                            setImportTripSunOpen(true);
                            setImportMenuOpen(false);
                          }}
                          className="flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-slate-50"
                        >
                          <span className="text-base">📋</span>
                          <span>
                            <div className="font-semibold text-slate-800">Caisse Trip Sun</div>
                            <div className="text-[11px] text-slate-500">
                              Immigration · 26 colonnes
                            </div>
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setImportB2COpen(true);
                            setImportMenuOpen(false);
                          }}
                          className="flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-slate-50"
                        >
                          <span className="text-base">📋</span>
                          <span>
                            <div className="font-semibold text-slate-800">Caisse Formation B2C</div>
                            <div className="text-[11px] text-slate-500">
                              Pizzalio, coiffure, plomberie
                            </div>
                          </span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            {canCreateOp && (
              <Button onClick={() => setCaisseDialogOpen(true)}>
                <Plus className="h-4 w-4" /> Nouvelle opération
              </Button>
            )}
          </div>
        }
      />

      {/* Sprint 6.9.8 — Dashboard simplifié : Mois en cours + Solde réel.
          Le détail par compte + ancien bandeau sont disponibles en accordion ci-dessous. */}
      <CaisseDashboardSimple
        entrepriseId={entrepriseId || null}
        entrepriseLabel={entreprises.find((e) => e.id === entrepriseId)?.nom ?? null}
      />

      <details className="group rounded-lg border border-slate-200 bg-white">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
          <span className="inline-flex items-center gap-2">
            <span className="text-base transition-transform group-open:rotate-90">▸</span>
            Indicateurs détaillés (solde par compte)
          </span>
          <span className="text-[10px] font-normal uppercase tracking-widest text-slate-500">
            Cliquer pour {soldes.length} compte{soldes.length > 1 ? 's' : ''}
          </span>
        </summary>
        <div className="border-t border-slate-200 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Solde global DZD (vue v_soldes_comptes — DZD actifs)
              </div>
              <div
                className={cn('num text-xl font-semibold', totalSoldeDzd < 0 && 'text-destructive')}
              >
                {formatDA(totalSoldeDzd)}
              </div>
            </div>
            <div className="ml-auto flex flex-wrap gap-2 text-xs">
              {soldes.map((s) => (
                <span
                  key={s.compte_id}
                  className="rounded-md border border-border bg-muted/30 px-2 py-1"
                >
                  <span className="font-medium">{s.intitule}</span>
                  <span className={cn('num ml-2', s.solde_courant < 0 && 'text-destructive')}>
                    {formatDA(s.solde_courant)}
                  </span>
                </span>
              ))}
              {soldes.length === 0 && (
                <span className="text-xs italic text-slate-500">
                  Aucun compte actif pour cette entreprise.
                </span>
              )}
            </div>
          </div>
        </div>
      </details>

      {/* Filtres */}
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-card p-3">
        <FilterSelect
          label={t('finances.operations.filters.sens')}
          value={filter.sens ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, sens: (v as OperationSens) || null }))}
          options={[
            { value: '', label: '— Tous —' },
            { value: 'entree', label: 'Entrée' },
            { value: 'sortie', label: 'Sortie' },
            { value: 'transfert', label: 'Transfert' },
          ]}
        />
        <FilterSelect
          label={t('finances.operations.filters.canal')}
          value={filter.canal ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, canal: (v as OperationCanal) || null }))}
          options={[
            { value: '', label: '— Tous —' },
            { value: 'especes', label: 'Espèces' },
            { value: 'ccp', label: 'CCP' },
            { value: 'banque', label: 'Banque' },
          ]}
        />
        <FilterSelect
          label={t('finances.operations.filters.statut')}
          value={filter.statut ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, statut: (v as OperationStatut) || null }))}
          options={[
            { value: '', label: '— Tous —' },
            { value: 'brouillon', label: 'Brouillon' },
            { value: 'en_attente', label: 'En attente' },
            { value: 'validee', label: 'Validée' },
            { value: 'approuvee', label: 'Approuvée' },
            { value: 'comptabilisee', label: 'Comptabilisée' },
            { value: 'annulee', label: 'Annulée' },
          ]}
        />
        <FilterSelect
          label={t('finances.operations.filters.activite')}
          value={filter.activiteId ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, activiteId: v || null }))}
          options={[
            { value: '', label: '— Toutes —' },
            ...activitesScoped.map((a) => ({ value: a.id, label: a.nom })),
          ]}
        />
        {/* Sprint 6.9.9 — Filtre Tier dynamique selon les tiers chargés pour l'entreprise */}
        <FilterSelect
          label="Tier"
          value={filter.tierId ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, tierId: v || null }))}
          options={[
            { value: '', label: '— Tous —' },
            ...tiers.slice(0, 100).map((t) => ({ value: t.id, label: t.raison_sociale })),
          ]}
        />
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            {t('finances.operations.filters.dateFrom')}
          </label>
          <Input
            type="date"
            value={filter.dateFrom ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, dateFrom: e.target.value || null }))}
            className="h-9 w-36"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">
            {t('finances.operations.filters.dateTo')}
          </label>
          <Input
            type="date"
            value={filter.dateTo ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, dateTo: e.target.value || null }))}
            className="h-9 w-36"
          />
        </div>
        <div className="ml-auto space-y-1">
          <label className="text-xs text-muted-foreground">Recherche</label>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Réf, motif, observation…"
            className="h-9 w-56"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setFilter({});
            setSearchInput('');
          }}
        >
          <XCircle className="h-3.5 w-3.5" /> Effacer
        </Button>
      </div>

      <DataTable
        key={selectionVersion}
        data={rows}
        columns={columns}
        loading={isLoading}
        pageSize={50}
        searchPlaceholder="Rechercher…"
        exportFileName="operations.csv"
        enableSelection
        getRowId={(row) => row.id}
        onSelectionChange={setSelectedRows}
        bulkBar={
          <BulkActionBar count={selectedRows.length} onClear={clearSelection}>
            <BulkButton onClick={() => exportSelectedCSV(selectedRows)}>
              <Download className="h-3.5 w-3.5" /> Exporter CSV
            </BulkButton>
            {allSelectedAreEnAttente && canValidate && (
              <BulkButton
                onClick={async () => {
                  const r = await bulkUpdateStatus.mutateAsync({
                    ids: selectedIds,
                    statut: 'validee',
                  });
                  reportBulk('valider', r);
                  clearSelection();
                }}
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Valider
              </BulkButton>
            )}
            {allSelectedAreValidee && canApprove && (
              <BulkButton
                onClick={async () => {
                  const r = await bulkUpdateStatus.mutateAsync({
                    ids: selectedIds,
                    statut: 'approuvee',
                  });
                  reportBulk('approuver', r);
                  clearSelection();
                }}
              >
                <ShieldCheck className="h-3.5 w-3.5" /> Approuver
              </BulkButton>
            )}
            {allSelectedAreApprouvee && canComptabiliser && (
              <BulkButton
                onClick={async () => {
                  const r = await bulkUpdateStatus.mutateAsync({
                    ids: selectedIds,
                    statut: 'comptabilisee',
                  });
                  reportBulk('comptabiliser', r);
                  clearSelection();
                }}
              >
                <FileCheck2 className="h-3.5 w-3.5" /> Comptabiliser
              </BulkButton>
            )}
            {allSelectedAreComptabilisee && canComptabiliser && (
              <BulkButton
                variant="destructive"
                onClick={async () => {
                  const r = await bulkContrePass.mutateAsync(selectedIds);
                  reportBulk('contre-passer', r);
                  clearSelection();
                }}
              >
                <RotateCcw className="h-3.5 w-3.5" /> Contre-passer
              </BulkButton>
            )}
            <BulkButton onClick={() => setBulkEditOpen(true)}>
              <Pencil className="h-3.5 w-3.5" /> Modifier en masse
            </BulkButton>
            {allSelectedAreSupprimables && isAdmin && (
              <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
                <Trash2 className="h-3.5 w-3.5" /> Supprimer
              </BulkButton>
            )}
          </BulkActionBar>
        }
      />

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total} opération{total > 1 ? 's' : ''} · page {page + 1} / {totalPages}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Précédent
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Suivant <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Sprint 6.8.1 — UNE seule modal "Nouvelle opération" (Caisse Groupe spec) */}
      <OperationCaisseFormDialog
        open={caisseDialogOpen}
        onOpenChange={setCaisseDialogOpen}
        entrepriseId={entrepriseId}
      />

      {/* Sprint 6.9.4 — imports CSV Caisse spécifiques */}
      <ImportCaisseTripSunDialog open={importTripSunOpen} onOpenChange={setImportTripSunOpen} />
      <ImportCaisseB2CDialog open={importB2COpen} onOpenChange={setImportB2COpen} />

      <ImportDialog<Record<string, unknown>>
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Importer des opérations financières (XLSX/CSV)"
        generateTemplate={() => buildTemplate(TEMPLATE_OPERATIONS)}
        templateFilename={TEMPLATE_OPERATIONS.filename}
        fields={
          [
            { key: 'date', label: 'Date opération', required: true },
            { key: 'type', label: 'Type', required: true },
            { key: 'sous_type', label: 'Sous-type' },
            { key: 'tier', label: 'Tier', required: true },
            { key: 'activite', label: 'Activité', required: true },
            { key: 'service', label: 'Service' },
            { key: 'motif', label: 'Motif' },
            { key: 'montant', label: 'Montant DZD', required: true },
            { key: 'devise', label: 'Devise', required: true },
            { key: 'canal', label: 'Mode paiement', required: true },
            { key: 'compte', label: 'Compte bancaire', required: true },
            { key: 'reference_externe', label: 'Référence externe' },
            { key: 'justificatif_url', label: 'Justificatif URL' },
            { key: 'observation', label: 'Observation' },
          ] satisfies ImportField[]
        }
        onImport={async (rows) => {
          // Sprint 6.9.7 — Refonte 2-passes : analyse → pre-flight UI → INSERT.
          // Pattern lookup-or-create pour les tiers (auto), bloqué pour activités/comptes
          // (création manuelle requise — la structure orga ne se touche pas depuis un import).

          // ===== Helpers locaux =====
          const norm = (s: unknown): string =>
            String(s ?? '')
              .trim()
              .toLowerCase();
          const parseDate = (raw: unknown): string | null => {
            if (raw == null || raw === '') return null;
            const s = String(raw).trim();
            if (!s) return null;
            const m1 = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
            if (m1) return `${m1[1]}-${m1[2]}-${m1[3]}`;
            const m2 = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/.exec(s);
            if (m2) {
              const [, d, mo, yRaw] = m2;
              const y = (yRaw ?? '').length === 2 ? `20${yRaw}` : (yRaw ?? '');
              return `${y}-${(mo ?? '').padStart(2, '0')}-${(d ?? '').padStart(2, '0')}`;
            }
            const dt = new Date(s);
            return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
          };
          const parseMontant = (raw: unknown): number => {
            if (raw == null || raw === '') return 0;
            if (typeof raw === 'number') return raw;
            const cleaned = String(raw).replace(/[^\d-]/g, '');
            if (!cleaned || cleaned === '-') return 0;
            const n = Number(cleaned);
            return isNaN(n) ? 0 : n;
          };
          // Levenshtein-like via substring + char-based similarity (simple & robuste)
          const similarity = (a: string, b: string): number => {
            const x = norm(a);
            const y = norm(b);
            if (!x || !y) return 0;
            if (x === y) return 1;
            if (x.includes(y) || y.includes(x)) return 0.85;
            const longer = x.length >= y.length ? x : y;
            const shorter = x.length >= y.length ? y : x;
            let common = 0;
            for (let i = 0; i < shorter.length; i++)
              if (longer.includes(shorter[i] ?? '')) common++;
            return common / longer.length;
          };

          // ===== Référentiels DB en mémoire =====
          const { data: tiersList = [] } = await supabase
            .from('tiers')
            .select('id, raison_sociale')
            .eq('entreprise_id', entrepriseId ?? '');
          const tierMap = new Map<string, string>();
          for (const t of (tiersList as { id: string; raison_sociale: string }[]) ?? [])
            tierMap.set(norm(t.raison_sociale), t.id);

          const actMap = new Map<string, string>();
          for (const a of allActivites) actMap.set(norm(a.nom), a.id);
          const actNames = allActivites.map((a) => a.nom);

          const { data: comptesList = [] } = await supabase
            .from('comptes_bancaires')
            .select('id, intitule');
          const compteMap = new Map<string, string>();
          for (const c of (comptesList ?? []) as { id: string; intitule: string }[])
            compteMap.set(norm(c.intitule), c.id);
          const compteNames = (comptesList as { intitule: string }[]).map((c) => c.intitule);

          const { data: caissesList = [] } = await supabase
            .from('caisses')
            .select('id, entreprise_id')
            .eq('entreprise_id', entrepriseId ?? '')
            .eq('is_active', true)
            .limit(1);
          const defaultCaisseId = (caissesList as { id: string }[])[0]?.id ?? null;

          // ===== PASSE 1 — analyse des rows + détection mismatches =====
          const missingActivites = new Map<string, number>();
          const missingComptes = new Map<string, number>();
          const newTiersByType = new Map<'client_b2c' | 'fournisseur', Map<string, true>>([
            ['client_b2c', new Map()],
            ['fournisseur', new Map()],
          ]);
          const invalidByReason = new Map<string, number>();
          let validRows = 0;
          const rowAnalysis: ({
            r: Record<string, unknown>;
            i: number;
            sens: OperationSens;
            canal: OperationCanal;
            activiteKey: string;
            tierKey: string;
            tierFallbackType: 'client_b2c' | 'fournisseur';
            compteKey: string;
            montant: number;
            dateIso: string;
          } | null)[] = [];

          for (let i = 0; i < rows.length; i++) {
            const raw = rows[i] as Record<string, unknown> | undefined;
            if (!raw) {
              rowAnalysis.push(null);
              continue;
            }
            const r = raw;

            const tierName = String(r.tier ?? '').trim();
            const actName = String(r.activite ?? '').trim();
            const compteName = String(r.compte ?? '').trim();
            const sens: OperationSens = norm(r.type) === 'sortie' ? 'sortie' : 'entree';
            const canalRaw = norm(r.canal);
            const canal: OperationCanal = canalRaw.startsWith('esp')
              ? 'especes'
              : canalRaw.includes('ccp')
                ? 'ccp'
                : 'banque';
            const montant = parseMontant(r.montant);
            const dateIso = parseDate(r.date);

            // Validations bloquantes par ligne (cumulables)
            const failReasons: string[] = [];
            if (!tierName) failReasons.push('Tier manquant');
            if (!actName) failReasons.push('Activité manquante');
            if (!montant || montant <= 0) failReasons.push('Montant invalide');
            if (!dateIso) failReasons.push('Date manquante ou format invalide');

            // Activité : doit exister (pas d'auto-create)
            if (actName && !actMap.has(norm(actName))) {
              missingActivites.set(actName, (missingActivites.get(actName) ?? 0) + 1);
            }

            // Compte : exigé seulement si canal != especes ; pas d'auto-create
            if (canal !== 'especes' && compteName && !compteMap.has(norm(compteName))) {
              missingComptes.set(compteName, (missingComptes.get(compteName) ?? 0) + 1);
            }
            if (canal === 'especes' && !defaultCaisseId) {
              failReasons.push('Aucune caisse active (créez-en une dans Paramètres)');
            }

            if (failReasons.length > 0) {
              for (const reason of failReasons)
                invalidByReason.set(reason, (invalidByReason.get(reason) ?? 0) + 1);
              rowAnalysis.push(null);
              continue;
            }

            // Tier : auto-create si introuvable. Type selon sens.
            const tierFallbackType: 'client_b2c' | 'fournisseur' =
              sens === 'entree' ? 'client_b2c' : 'fournisseur';
            const tierKey = norm(tierName);
            if (!tierMap.has(tierKey)) {
              newTiersByType.get(tierFallbackType)?.set(tierKey, true);
            }

            validRows++;
            rowAnalysis.push({
              r,
              i,
              sens,
              canal,
              activiteKey: norm(actName),
              tierKey,
              tierFallbackType,
              compteKey: norm(compteName),
              montant,
              dateIso: dateIso as string,
            });
          }

          // ===== Pre-flight UI =====
          const missingActivitesArr = Array.from(missingActivites.entries()).map(
            ([name, lines]) => ({
              name,
              lines,
            }),
          );
          const missingComptesArr = Array.from(missingComptes.entries()).map(([name, lines]) => ({
            name,
            lines,
          }));
          const fuzzy: PreflightSummary['fuzzySuggestions'] = [];
          for (const m of missingActivitesArr) {
            let best: { name: string; score: number } | null = null;
            for (const n of actNames) {
              const score = similarity(m.name, n);
              if (score >= 0.7 && (!best || score > best.score)) best = { name: n, score };
            }
            if (best) fuzzy.push({ missing: m.name, suggestion: best.name, kind: 'activite' });
          }
          for (const m of missingComptesArr) {
            let best: { name: string; score: number } | null = null;
            for (const n of compteNames) {
              const score = similarity(m.name, n);
              if (score >= 0.7 && (!best || score > best.score)) best = { name: n, score };
            }
            if (best) fuzzy.push({ missing: m.name, suggestion: best.name, kind: 'compte' });
          }

          const summary: PreflightSummary = {
            totalRows: rows.length,
            validRows,
            missingActivites: missingActivitesArr,
            missingComptes: missingComptesArr,
            newTiersClient: newTiersByType.get('client_b2c')?.size ?? 0,
            newTiersFournisseur: newTiersByType.get('fournisseur')?.size ?? 0,
            invalidLines: Array.from(invalidByReason.entries()).map(([reason, count]) => ({
              reason,
              count,
            })),
            fuzzySuggestions: fuzzy,
          };

          const decision = await askPreflight(summary);
          if (decision === 'cancel') {
            return { success: 0, errors: ["Import annulé par l'utilisateur"] };
          }

          // ===== Bloquant si activités ou comptes manquants =====
          if (missingActivitesArr.length > 0) {
            const list = missingActivitesArr
              .slice(0, 5)
              .map((m) => `${m.name} (${m.lines} lignes)`)
              .join(' · ');
            return {
              success: 0,
              errors: [`Activités manquantes — créez-les dans Paramètres > Activités : ${list}`],
            };
          }
          if (missingComptesArr.length > 0) {
            const list = missingComptesArr
              .slice(0, 5)
              .map((m) => `${m.name} (${m.lines} lignes)`)
              .join(' · ');
            return {
              success: 0,
              errors: [
                `Comptes bancaires manquants — créez-les dans Paramètres > Banques & Caisses : ${list}`,
              ],
            };
          }

          // ===== PASSE 2 — INSERT batch tiers manquants (chunks 500) =====
          const tiersToCreate: { type: 'client_b2c' | 'fournisseur'; name: string }[] = [];
          for (const [type, set] of newTiersByType) {
            for (const name of set.keys()) {
              tiersToCreate.push({ type, name });
            }
          }
          // Récupère le nom original (casing) depuis la première analyse qui matche
          const firstSeenName = new Map<string, string>();
          for (const a of rowAnalysis) {
            if (!a) continue;
            if (!firstSeenName.has(a.tierKey))
              firstSeenName.set(a.tierKey, String(a.r.tier ?? '').trim());
          }

          const errors: string[] = [];
          const errorReasons = new Map<string, number>();
          const errorSamples = new Map<string, string[]>();
          const addError = (line: number, reason: string, detail?: string): void => {
            const full = detail ? `L${line + 2}: ${reason} — ${detail}` : `L${line + 2}: ${reason}`;
            errors.push(full);
            errorReasons.set(reason, (errorReasons.get(reason) ?? 0) + 1);
            const samples = errorSamples.get(reason) ?? [];
            if (samples.length < 5) samples.push(full);
            errorSamples.set(reason, samples);
          };

          // Récupère l'activite_id par nom + entreprise_id (ils existent tous, validé ci-dessus)
          const tierTypeByKey = new Map<string, 'client_b2c' | 'fournisseur'>();
          for (const a of rowAnalysis) {
            if (a) tierTypeByKey.set(a.tierKey, a.tierFallbackType);
          }

          // Insert tiers en chunks de 500
          if (tiersToCreate.length > 0) {
            const chunks: (typeof tiersToCreate)[] = [];
            for (let i = 0; i < tiersToCreate.length; i += 500)
              chunks.push(tiersToCreate.slice(i, i + 500));
            for (const chunk of chunks) {
              const payload = chunk.map((t) => ({
                entreprise_id: entrepriseId,
                type: t.type,
                raison_sociale: firstSeenName.get(t.name) ?? t.name,
              }));
              const { data, error } = await supabase
                .from('tiers')
                .insert(payload as never)
                .select('id, raison_sociale');
              if (error) {
                // Échec batch : reporter en erreur mais continuer
                errors.push(`Insert tiers KO : ${error.message}`);
                continue;
              }
              for (const row of (data ?? []) as { id: string; raison_sociale: string }[]) {
                tierMap.set(norm(row.raison_sociale), row.id);
              }
            }
          }

          // ===== PASSE 3 — INSERT operations en batch chunks 500 =====
          const opPayloads: Record<string, unknown>[] = [];
          const opLineIndex: number[] = [];
          for (const a of rowAnalysis) {
            if (!a) continue;
            const tierId = tierMap.get(a.tierKey);
            if (!tierId) {
              addError(a.i, 'Tier non créé', a.tierKey);
              continue;
            }
            const activiteId = actMap.get(a.activiteKey);
            if (!activiteId) {
              addError(a.i, 'Activité introuvable (race condition)', a.activiteKey);
              continue;
            }
            let cible: { caisse_id: string | null; compte_bancaire_id: string | null };
            if (a.canal === 'especes') {
              if (!defaultCaisseId) {
                addError(a.i, 'Aucune caisse active');
                continue;
              }
              cible = { caisse_id: defaultCaisseId, compte_bancaire_id: null };
            } else {
              const compteId = compteMap.get(a.compteKey);
              if (!compteId) {
                addError(a.i, 'Compte bancaire introuvable', String(a.r.compte ?? ''));
                continue;
              }
              cible = { caisse_id: null, compte_bancaire_id: compteId };
            }

            const motif = String(
              a.r.motif ?? a.r.service ?? a.r.observation ?? 'Opération importée',
            ).trim();
            const obsParts = [
              a.r.service && `Service : ${a.r.service}`,
              a.r.reference_externe && `Réf : ${a.r.reference_externe}`,
              a.r.observation,
            ].filter(Boolean);

            opPayloads.push({
              entreprise_id: entrepriseId,
              activite_id: activiteId,
              activite_beneficiaire_id: activiteId,
              activite_emettrice_id: activiteId,
              tier_id: tierId,
              date_operation: a.dateIso,
              sens: a.sens,
              canal: a.canal,
              ...cible,
              categorie: 'autre',
              devise_code: String(a.r.devise ?? 'DZD')
                .trim()
                .toUpperCase(),
              montant: a.montant,
              taux_change: 1,
              montant_dzd: a.montant,
              motif: motif.slice(0, 500),
              observation: obsParts.length > 0 ? obsParts.join(' · ') : null,
              reference: String(a.r.reference_externe ?? '').trim() || null,
              statut: 'validee' as OperationStatut,
            });
            opLineIndex.push(a.i);
          }

          let success = 0;
          for (let i = 0; i < opPayloads.length; i += 500) {
            const chunk = opPayloads.slice(i, i + 500);
            const { error, data } = await supabase
              .from('operations')
              .insert(chunk as never)
              .select('id');
            if (error) {
              // Fallback : insert ligne par ligne pour identifier les rejets
              for (let j = 0; j < chunk.length; j++) {
                const { error: errSingle } = await supabase
                  .from('operations')
                  .insert(chunk[j] as never);
                if (errSingle) addError(opLineIndex[i + j] ?? -1, 'INSERT KO', errSingle.message);
                else success++;
              }
            } else {
              success += (data ?? []).length;
            }
          }

          // Toast groupé
          if (errorReasons.size > 0 || invalidByReason.size > 0) {
            const grouped: { reason: string; count: number; samples: string[] }[] = [];
            for (const [reason, count] of invalidByReason) {
              grouped.push({ reason, count, samples: [`(détecté en pré-vol)`] });
            }
            for (const [reason, count] of errorReasons) {
              grouped.push({ reason, count, samples: errorSamples.get(reason) ?? [] });
            }
            setErrorDetail({ grouped, flat: errors });
          }

          return { success, errors };
        }}
      />

      {/* Sprint 6.9.9 — Modal édition d'une opération existante (date / sens / catégorie /
          montant / motif / observation / tier / activité / statut). Canal et cible figés. */}
      <EditOperationDialog
        open={!!editOperation}
        onOpenChange={(o) => !o && setEditOperation(null)}
        operation={editOperation}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer l'opération « ${confirmDelete?.reference} » ?`}
        description="Cette action est irréversible (uniquement possible sur un brouillon)."
        loading={removeOp.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await removeOp.mutateAsync(confirmDelete.id);
            toast.success('Opération supprimée');
          } catch (e) {
            toast.error(`Échec : ${(e as Error).message}`);
            return;
          }
          setConfirmDelete(null);
        }}
      />

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selectedRows.length} opération${selectedRows.length > 1 ? 's' : ''} ?`}
        description="Action irréversible. Seuls les brouillons peuvent être supprimés."
        consequences={[
          `${selectedRows.length} ligne(s) seront définitivement effacées`,
          'Les justificatifs liés ne seront pas supprimés (à gérer séparément)',
          "L'action est journalisée dans audit_log",
        ]}
        challenge="SUPPRIMER"
        loading={bulkDelete.isPending}
        onConfirm={async () => {
          const r = await bulkDelete.mutateAsync(selectedIds);
          reportBulk('supprimer', r);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />

      <BulkEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        table="operations"
        ids={selectedIds}
        fields={
          [
            {
              key: 'categorie',
              label: 'Catégorie',
              type: 'select',
              options: Object.entries(CATEGORIE_LABELS).map(([value, label]) => ({ value, label })),
            },
            {
              key: 'activite_id',
              label: 'Activité',
              type: 'select',
              options: [
                { value: '', label: '— Aucune —' },
                ...activitesScoped.map((a) => ({ value: a.id, label: a.nom })),
              ],
            },
            {
              key: 'tier_id',
              label: 'Tiers',
              type: 'select',
              options: [
                { value: '', label: '— Aucun —' },
                ...tiers.slice(0, 200).map((t2) => ({
                  value: t2.id,
                  label: `${t2.code ? t2.code + ' · ' : ''}${t2.raison_sociale}`,
                })),
              ],
            },
            { key: 'observation', label: 'Observation', type: 'textarea' },
          ] satisfies BulkEditField[]
        }
        onApplied={() => {
          clearSelection();
        }}
      />

      {/* Sprint 6.9.7 — Pre-flight import dialog (récap avant INSERT) */}
      {preflight && (
        <ConfirmDialog
          open={true}
          onOpenChange={() => resolvePreflight('cancel')}
          destructive={preflight.missingActivites.length > 0 || preflight.missingComptes.length > 0}
          title={
            preflight.missingActivites.length > 0 || preflight.missingComptes.length > 0
              ? '❌ Import bloqué'
              : `📋 Récapitulatif import — ${preflight.validRows} ligne${preflight.validRows > 1 ? 's' : ''} valide${preflight.validRows > 1 ? 's' : ''} sur ${preflight.totalRows}`
          }
          description={
            preflight.missingActivites.length > 0 || preflight.missingComptes.length > 0
              ? [
                  preflight.missingActivites.length > 0
                    ? `Activités manquantes : ${preflight.missingActivites
                        .slice(0, 5)
                        .map((m) => `${m.name} (${m.lines} lignes)`)
                        .join(', ')}`
                    : '',
                  preflight.missingComptes.length > 0
                    ? `Comptes bancaires manquants : ${preflight.missingComptes
                        .slice(0, 5)
                        .map((m) => `${m.name} (${m.lines} lignes)`)
                        .join(', ')}`
                    : '',
                  preflight.fuzzySuggestions.length > 0
                    ? `Suggestions (proche match) : ${preflight.fuzzySuggestions
                        .slice(0, 3)
                        .map((s) => `'${s.missing}' → '${s.suggestion}' ?`)
                        .join(' · ')}`
                    : '',
                  'Créez-les dans Paramètres puis recommencez.',
                ]
                  .filter(Boolean)
                  .join('\n\n')
              : [
                  `À CRÉER automatiquement :`,
                  preflight.newTiersClient > 0
                    ? `• ${preflight.newTiersClient} nouveau${preflight.newTiersClient > 1 ? 'x' : ''} tier${preflight.newTiersClient > 1 ? 's' : ''} client_b2c (Entrées)`
                    : null,
                  preflight.newTiersFournisseur > 0
                    ? `• ${preflight.newTiersFournisseur} nouveau${preflight.newTiersFournisseur > 1 ? 'x' : ''} tier${preflight.newTiersFournisseur > 1 ? 's' : ''} fournisseur (Sorties)`
                    : null,
                  preflight.invalidLines.length > 0
                    ? `\nLignes ignorées (${preflight.invalidLines.reduce((a, l) => a + l.count, 0)}) : ${preflight.invalidLines.map((l) => `${l.count}× ${l.reason}`).join(' · ')}`
                    : null,
                ]
                  .filter(Boolean)
                  .join('\n')
          }
          confirmLabel={
            preflight.missingActivites.length > 0 || preflight.missingComptes.length > 0
              ? 'Fermer'
              : 'Confirmer et importer'
          }
          onConfirm={async () => {
            const isBlocked =
              preflight.missingActivites.length > 0 || preflight.missingComptes.length > 0;
            resolvePreflight(isBlocked ? 'cancel' : 'confirm');
          }}
        />
      )}

      {/* Sprint 6.9.7 — Détail des erreurs groupées par cause */}
      <ConfirmDialog
        open={!!errorDetail}
        onOpenChange={(o) => !o && setErrorDetail(null)}
        title={`Détail des erreurs — ${errorDetail?.flat.length ?? 0} ligne(s)`}
        description={
          errorDetail
            ? errorDetail.grouped
                .map(
                  (g) =>
                    `${g.count}× ${g.reason}${g.samples.length > 0 ? `\n   ex: ${g.samples[0]}` : ''}`,
                )
                .join('\n\n')
            : ''
        }
        confirmLabel="Fermer"
        onConfirm={async () => setErrorDetail(null)}
      />
    </div>
  );
}

function exportSelectedCSV(rows: OperationRow[]): void {
  if (rows.length === 0) return;
  const headers = [
    'reference',
    'date_operation',
    'sens',
    'canal',
    'categorie',
    'motif',
    'montant_dzd',
    'statut',
  ];
  const csvRows = rows.map((r) =>
    headers
      .map((h) => {
        const v = (r as unknown as Record<string, unknown>)[h];
        const s = v == null ? '' : String(v);
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      })
      .join(','),
  );
  const csv = [headers.join(','), ...csvRows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `operations-${rows.length}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`${rows.length} ligne(s) exportée(s)`);
}

function reportBulk(
  action: string,
  result: { success: string[]; errors: { id: string; message: string }[] },
): void {
  if (result.errors.length === 0) {
    toast.success(
      `${result.success.length} opération${result.success.length > 1 ? 's' : ''} ${action}${action.endsWith('e') ? 'e' : action.endsWith('r') ? 'ée' : 'es'}`,
    );
  } else {
    toast.warning(
      `${result.success.length} OK · ${result.errors.length} échec(s) — ex: ${result.errors[0]?.message ?? ''}`,
    );
  }
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}): JSX.Element {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
